import { Injectable, Injector, effect, inject, signal } from '@angular/core';
import type { Ubicacion, FingerprintId, DeviceInfo } from '@core/remito.model';
import { CheckInLocationDbService, type PendingLocation } from './checkin-location-db';
import { SessionLifecycleService } from './session-lifecycle';
import { BusyStateService } from '@shared/services/update/busy-state';
import { LogService } from './log.service';

@Injectable({ providedIn: 'root' })
export class CheckInLocationSyncService {
  private readonly db = inject(CheckInLocationDbService);
  private readonly injector = inject(Injector);
  private readonly busyState = inject(BusyStateService);
  private readonly logger = inject(LogService);
  private sessionLifecycle: SessionLifecycleService | null = null;

  readonly storedCount = signal(0);

  private dbInitialized = false;

  constructor() {
    void this.ensureDbInitialized();

    effect(() => {
      this.busyState.setCheckInSessionActive(this.storedCount() > 0);
    });
  }

  private getSessionLifecycleService(): SessionLifecycleService {
    if (!this.sessionLifecycle) {
      this.sessionLifecycle = this.injector.get(SessionLifecycleService);
    }
    return this.sessionLifecycle;
  }

  async registrar(
    ubicacion: Ubicacion,
    fingerprintId?: FingerprintId,
    deviceInfo?: DeviceInfo
  ): Promise<PendingLocation> {
    if (!ubicacion) {
      throw new Error('La ubicación es requerida para registrar el Check-in');
    }

    const sessionLifecycle = this.getSessionLifecycleService();
    const sessionId = sessionLifecycle.getSessionId() || 'anonymous-' + Date.now();

    this.logger.info('[CheckInLocationSync] Iniciando registro de check-in', {
      hasLocation: !!ubicacion,
      hasSessionId: !!sessionId,
      hasFingerprintId: !!fingerprintId,
      hasDeviceInfo: !!deviceInfo
    });

    await this.ensureDbInitialized();
    const existingCount = await this.db.getCount();
    if (existingCount > 0) {
      throw new Error('Ya existe un Check-in en curso. Completa el Check-out antes de iniciar uno nuevo.');
    }

    const pending = await this.db.savePending({
      id: this.generateId(),
      ubicacion,
      sessionId,
      fingerprintId,
      deviceInfo
    });

    this.logger.info('[CheckInLocationSync] Check-in guardado exitosamente en IndexedDB', {
      hasId: !!pending.id,
      status: pending.status
    });

    await this.refreshStoredCount();
    return pending;
  }

  async getStoredCheckIn(): Promise<PendingLocation | null> {
    await this.ensureDbInitialized();
    const pending = await this.db.getPending();
    if (!pending.length) {
      return null;
    }
    return pending.sort((a, b) => b.createdAt - a.createdAt)[0];
  }

  async clearStoredCheckIns(): Promise<void> {
    await this.ensureDbInitialized();
    await this.db.clearAll();
    await this.refreshStoredCount();
  }

  private async ensureDbInitialized(): Promise<void> {
    if (this.dbInitialized) {
      return;
    }
    await this.db.init();
    await this.refreshStoredCount();
    this.dbInitialized = true;
  }

  private async refreshStoredCount(): Promise<void> {
    const count = await this.db.getCount();
    this.storedCount.set(count);
  }

  private generateId(): string {
    if (globalThis.crypto?.randomUUID) {
      return globalThis.crypto.randomUUID();
    }
    return `checkin-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
